<template>
  <div>
    <el-tabs v-model="menu">
      <el-tab-pane name="agreement">
        <span slot="label"><i class="fa fa-file-signature text-muted"></i> Sharer Agreement</span>

        <el-row>
          <el-col :xs="24" :sm="12" :md="6">
            Agreement Start <strong>{{formatDate(details.started)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Expiry <strong>{{formatDate(details.expiry)}}</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Agreement Term <strong>{{this.term}} months</strong>
          </el-col>

          <el-col :xs="24" :sm="12" :md="6">
            Expires <strong>{{this.expiry}}</strong>
          </el-col>
        </el-row>

        <p>
            <h3>Site Financials</h3>
            <el-button v-if="isManager('finance|project') && !readOnly" type="text" @click="editState()"><i :class="editing ? 'fa fa-check-circle text-success' : 'fa fa-edit'"></i></el-button>
            <el-button v-if="this.editing" type="text" @click="editing = false"><i class="fa fa-times-circle text-danger"></i></el-button>
        </p>

        <el-row>
          <el-col :span="24">
            <el-form inline label-position="top" ref='financeForm' :model='details'>

              <el-row :gutter="12">
                <el-col :span="24">
                  <select-sharer-status v-model="details.status" :disabled="!editing" />
                  <select-sharer-rates v-model="details.rate_id" :sharer-id="sharer.organisation_id" :disabled="!editing"/>

                  <date-input v-if="details.started || editing" label="Started" v-model="details.started" :disabled="!editing" />
                  <date-input v-if="details.status !== 'Terminated' && (details.startBilling || editing)" label="Billing from" v-model="details.startBilling" :disabled="!editing" />
                  <date-input v-if="details.status == 'Terminated' && (details.terminated || editing)" label="Terminated" v-model="details.terminated" :disabled="!editing" />
                  <date-input v-if="details.expiry || editing" label="Expiry" v-model="details.expiry" :disabled="!editing" />

                </el-col>
              </el-row>

              <el-alert v-if="details && editing" type="warning" title="Sharer specific!" description="Only input values PER sharer. For site wide CAPEX and OPEX see Finance Summary." show-icon  :closable="false" />
                <el-row :gutter="12">
                    <el-col :span="24">
                      <currency-input v-if="details.currentRate || editing" label="Current Fees" v-model="details.currentRate" :disabled="!editing" />
                      <el-tooltip content="If checked, the rate will be locked. When you manually adjust the rate, if will be locked." position="bottom">
                        <check-box v-if="details.rateLocked || editing" label="Rate Locked" v-model="details.rateLocked" :disabled="!editing" />
                      </el-tooltip>

                      <float-input
                        v-if="details.discount || editing"
                        v-model="details.discount"
                        label="Discount" :prefix="details.discountPercentage ? currency : ''"
                        :suffix="details.discountPercentage ? '' : '%'"
                        :disabled="!editing" />

                        <check-box
                          v-if="editing || details.discount"
                          label="Disc. Type"
                          v-model="details.discountPercentage"
                          :disabled="!editing">
                          {{!details.discountPercentage ? '%' : currency}}
                        </check-box>

                        <float-input
                            v-if="details.interestRate || editing"
                            v-model="details.interestRate"
                            label="Interest Rate"
                            suffix="%"
                            :disabled="!editing" />

                      <float-input v-if="details.capex || editing" label="Sharer Specific CAPEX" v-model="details.capex" :decimals="2" :disabled="!editing" />
                      <float-input v-if="details.opex || editing" label="Sharer Specific OPEX" v-model="details.opex" :decimals="2" :disabled="!editing"  />
                </el-col>

              </el-row>

              <div v-if="details.updated_at">
                <small class="text-muted pull-right">Last update: <b>{{formatFriendlyDate(details.updated_at)}}</b><span v-if="details.updated_by"> by {{details.updated_by}}</span></small>
              </div>

          </el-form>

        </el-col>
      </el-row>

      <el-row v-if="canAccess('finance') && sharer.site_id && sharer.id">
        <el-col :span="24">
          <files-panel title="License Agreement" type="site" :id="sharer.site_id" process="sharer" :process-id="sharer.id" hide-carousel />
        </el-col>
      </el-row>

   </el-tab-pane>


   <el-tab-pane name="sharerInvoice" lazy>
     <span slot="label"><i class="fa fa-money-check text-muted"></i> Sharer Invoice</span>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" />
   </el-tab-pane>

   <el-tab-pane name="sharerVO" lazy>
     <span slot="label"><i class="fa fa-screwdriver text-muted"></i> Sharer V.O.</span>

     <site-invoice :site-data="siteData" :sharer-id="sharer.organisation_id" :org="sharer" is-vo />
   </el-tab-pane>


   <el-tab-pane name="billableEquip" lazy>
     <span slot="label"><i class="fa fa-tools text-muted"></i> Billable Equip.</span>
     <billable-equip-table :site-id="details.site_id" :site-data="siteData" :owner-id="sharer.organisation_id" />
   </el-tab-pane>

 </el-tabs>
  </div>
</template>


<script>

import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'

import moment from 'moment'

import {settings} from '@/components/Data'

import SelectSharerStatus from '@/components/atoms/selects/SelectSharerStatus'
import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'

import BillableEquipTable from '@/components/assets/subcat/BillableEquipTable'

import SiteInvoice from '@/components/sites/finance/SiteInvoice'

export default {
  mixins: [DateMixins, NumberMixins, PostMixins],

  props: {
    sharer: {
      type: Object,
      required: true
    },

    editMode: {
      type: Boolean,
      default: false
    },

    siteData: {
      type: Object
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    BillableEquipTable,
    SelectSharerStatus,
    SiteInvoice,
    SelectSharerRates,
  },

  computed: {
    term: function () {
      return moment(this.details.expiry).diff(this.details.started, 'months')
    },

    expiry: function () {
      return moment(this.details.expiry).fromNow()
    }
  },

  methods: {
    saveSucccess (data) {
      this.originalRate = data.currentRate
    },

    editState () {
      if (this.editing) {

        if (this.originalRate !== this.details.currentRate) {
          this.details.rateLocked = true
        }

        this.details.started = this.formatDateSql(this.details.started)
        this.details.startBilling = this.formatDateSql(this.details.startBilling)
        this.details.expiry = this.formatDateSql(this.details.expiry)
        this.details.terminated = this.formatDateSql(this.details.terminated)

        this.saveForm(this, '/licensees', this.details.id, this.details, 'Sharer Financial Data', this.saveSucccess)
        this.editing = false
      } else {
        this.editing = true
      }
    }
  },

  watch: {
    details: function (data) {
      this.details = data
    }
  },

  data () {
    return {
      menu: 'agreement',
      editing: this.editMode,
      currency: settings.currency,
      details: this.clone(this.sharer),
      originalRate: this.sharer.currentRate
    }
  }
}

</script>



// WEBPACK FOOTER //
// FinanceSharer.vue?48ed4e40